import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatTime, formatDuration } from '../hooks/useSupabase'
import { Button, Card, Badge, PageHeader, Modal } from '../components/ui/SharedUI'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const TYPE_COLORS = {
  direct_care: '#2C7A7B',
  no_show_covered: '#E07856',
  training: '#8b5000',
  waiting_period: '#0F6E56',
}

export function CalendarPage() {
  const { user } = useAuth()
  const { data: shifts } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: true })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id' })

  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r.name]))

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days = []

    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const dayShifts = shifts.filter(s => s.date === dateStr && !s.is_active)
      const totalHours = dayShifts.reduce((sum, s) => {
        if (!s.clock_out) return sum
        return sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000
      }, 0)
      days.push({ day: d, dateStr, shifts: dayShifts, totalHours })
    }
    return days
  }, [year, month, shifts])

  const monthStats = useMemo(() => {
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`
    const monthShifts = shifts.filter(s => s.date.startsWith(monthStr) && s.clock_out && !s.is_active)
    const totalHours = monthShifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    return { totalShifts: monthShifts.length, totalHours }
  }, [year, month, shifts])

  function prevMonth() {
    setCurrentDate(new Date(year, month - 1, 1))
  }
  function nextMonth() {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const today = new Date().toISOString().split('T')[0]
  const selectedDayData = selectedDay ? calendarDays.find(d => d?.dateStr === selectedDay) : null

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <PageHeader title="Calendar" description="Monthly view of all your shifts." />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <Button variant="ghost" onClick={prevMonth}><ChevronLeft size={18} /></Button>
          <div>
            <span style={{ fontSize: '18px', fontWeight: 500, color: '#1A202C', fontFamily: "'Playfair Display', Georgia, serif" }}>
              {monthName}
            </span>
            <span style={{ fontSize: '13px', color: '#718096', marginLeft: '12px' }}>
              {monthStats.totalShifts} shifts · {monthStats.totalHours.toFixed(1)}h
            </span>
          </div>
          <Button variant="ghost" onClick={nextMonth}><ChevronRight size={18} /></Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: '#EDF2F7', borderRadius: '8px', overflow: 'hidden' }}>
          {DAYS.map(d => (
            <div key={d} style={{ padding: '8px', textAlign: 'center', fontSize: '12px', fontWeight: 500, color: '#A0AEC0', background: '#F7FAFC' }}>
              {d}
            </div>
          ))}
          {calendarDays.map((cell, i) => (
            <div
              key={i}
              onClick={() => cell && cell.shifts.length > 0 && setSelectedDay(cell.dateStr)}
              style={{
                padding: '6px',
                minHeight: '80px',
                background: cell?.dateStr === today ? '#F0FFF4' : '#FFFFFF',
                cursor: cell?.shifts.length > 0 ? 'pointer' : 'default',
              }}
            >
              {cell && (
                <>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: cell.dateStr === today ? 600 : 400,
                    color: cell.dateStr === today ? '#2C7A7B' : '#4A5568',
                    marginBottom: '4px',
                  }}>
                    {cell.day}
                  </div>
                  {cell.shifts.slice(0, 3).map(s => {
                    const isObserved = s.authorship === 'observed'
                    const caregiverLabel = isObserved
                      ? s.observed_caregiver_name || 'Unknown'
                      : 'Me'
                    const chipColor = isObserved ? '#8b5000' : (TYPE_COLORS[s.shift_type] || '#2C7A7B')
                    const chipBg = isObserved ? '#FCF5E8' : `${TYPE_COLORS[s.shift_type] || '#2C7A7B'}15`

                    return (
                      <div key={s.id} style={{
                        fontSize: '11px',
                        padding: '3px 5px',
                        borderRadius: '3px',
                        background: chipBg,
                        color: chipColor,
                        marginBottom: '2px',
                        overflow: 'hidden',
                        borderLeft: `2px solid ${chipColor}`,
                        lineHeight: 1.3,
                      }}>
                        <div style={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {caregiverLabel}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          opacity: 0.85,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {formatTime(s.clock_in)}
                        </div>
                      </div>
                    )
                  })}
                  {cell.shifts.length > 3 && (
                    <div style={{ fontSize: '11px', color: '#A0AEC0' }}>+{cell.shifts.length - 3} more</div>
                  )}
                  {cell.totalHours > 0 && (
                    <div style={{ fontSize: '11px', color: '#A0AEC0', marginTop: '2px' }}>
                      {cell.totalHours.toFixed(1)}h
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Modal open={!!selectedDay} onClose={() => setSelectedDay(null)} title={selectedDay ? new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ''}>
        {selectedDayData?.shifts.map(s => {
          const isObserved = s.authorship === 'observed'
          const accentColor = isObserved ? '#8b5000' : (TYPE_COLORS[s.shift_type] || '#2C7A7B')
          const caregiverName = isObserved
            ? s.observed_caregiver_name || 'Unknown caregiver'
            : 'You'

          return (
            <Card key={s.id} style={{
              marginBottom: '8px',
              borderLeft: `3px solid ${accentColor}`,
              background: isObserved ? '#FDFCF7' : undefined,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '2px' }}>
                    {caregiverName}
                  </div>
                  <div style={{ fontSize: '13px', color: '#718096' }}>
                    {formatTime(s.clock_in)} – {s.clock_out ? formatTime(s.clock_out) : 'in progress'} · {formatDuration(s.clock_in, s.clock_out)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {isObserved && <Badge variant="amber">Observed</Badge>}
                  <Badge>{s.shift_type.replace(/_/g, ' ')}</Badge>
                </div>
              </div>
              {isObserved && s.evidence_source && (
                <div style={{ fontSize: '12px', color: '#8b5000', marginBottom: '4px' }}>
                  Basis: {s.evidence_source.replace(/_/g, ' ')}
                  {s.evidence_source_note && ` — ${s.evidence_source_note}`}
                </div>
              )}
              {s.recipient_id && (
                <div style={{ fontSize: '13px', color: '#718096' }}>
                  Care recipient: {recipientMap[s.recipient_id] || ''}
                </div>
              )}
              {s.notes && <div style={{ fontSize: '13px', color: '#4A5568', marginTop: '6px', fontStyle: 'italic' }}>{s.notes}</div>}
              {s.tasks?.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                  {s.tasks.map(t => <Badge key={t} variant="gray">{t.replace(/_/g, ' ')}</Badge>)}
                </div>
              )}
            </Card>
          )
        })}
      </Modal>
    </div>
  )
}
